import axios from 'axios'

// 配置axios基础URL：按环境自动切换
// npm run dev → development → 本地;  npm run build → production → 线上
axios.defaults.baseURL = process.env.NODE_ENV === 'production'
  ? 'https://chb.bjzkbh.com/api'
  : '/api'

// 请求拦截器 - 添加token
axios.interceptors.request.use(config => {
  const token = sessionStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// 响应拦截器 - 全局错误处理
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response) {
      const status = error.response.status
      const data = error.response.data
      const message = (data && data.message) || '请求失败'
      if (status === 401) {
        // 401 在 main.js 的全局拦截器统一处理（清本地登录态 + router 跳转）
      } else if (status === 403) {
        import('element-ui').then(({ Message }) => Message.error('无权限访问'))
      } else if (status >= 500) {
        import('element-ui').then(({ Message }) => Message.error('服务器错误: ' + message))
      }
    } else if (error.request) {
      import('element-ui').then(({ Message }) => Message.error('网络连接失败，请检查网络'))
    }
    return Promise.reject(error)
  }
)

// 认证相关API
export const authApi = {
  login: (username, password) => axios.post('/auth/login', { username, password, clientType: 'web' }),
  register: (user) => axios.post('/auth/register', user),
  getCurrentUser: (token) => axios.get('/auth/me', { headers: { Authorization: `Bearer ${token}` } })
}

// 数据看板相关API
export const dashboardApi = {
  getStats: () => axios.get('/dashboard/stats'),
  getTasks: () => axios.get('/dashboard/tasks'),
  getAlerts: () => axios.get('/dashboard/alerts')
}

// 产品相关API（默认排除礼品类目）
export const productApi = {
  getProductList: (includeGift) => axios.get('/product/list', { params: { includeGift: !!includeGift } }),
  getProductPage: (page, size, name, categoryId, applicableTypeId) => axios.get('/product/page', { params: { page, size, name, categoryId, applicableTypeId } }),
  getProductById: (id) => axios.get(`/product/${id}`),
  addProduct: (product) => axios.post('/product', product),
  updateProduct: (product) => axios.put('/product', product),
  deleteProduct: (id) => axios.delete(`/product/${id}`)
}

// 适用类型相关API
export const applicableTypeApi = {
  getApplicableTypeList: (enabled) => axios.get('/applicable-type/list', { params: enabled != null ? { enabled } : {} }),
  addApplicableType: (type) => axios.post('/applicable-type', type),
  updateApplicableType: (type) => axios.put('/applicable-type', type),
  deleteApplicableType: (id) => axios.delete(`/applicable-type/${id}`)
}

// 礼品管理API（独立入口，自动归属礼品类目）
export const giftApi = {
  getGiftList: () => axios.get('/gift/list'),
  getGiftPage: (page, size, name) => axios.get('/gift/page', { params: { page, size, name } }),
  getGiftById: (id) => axios.get(`/gift/${id}`),
  addGift: (gift) => axios.post('/gift', gift),
  updateGift: (gift) => axios.put('/gift', gift),
  deleteGift: (id) => axios.delete(`/gift/${id}`)
}

// 分类相关API
export const categoryApi = {
  getCategoryList: () => axios.get('/category/list'),
  getCategoryById: (id) => axios.get(`/category/${id}`),
  addCategory: (category) => axios.post('/category', category),
  updateCategory: (category) => axios.put('/category', category),
  deleteCategory: (id) => axios.delete(`/category/${id}`)
}

// 厂家相关API
export const manufacturerApi = {
  getList: () => axios.get('/manufacturer/list'),
  getPage: (page, size, params) => axios.get('/manufacturer/page', { params: { page, size, ...(params || {}) } }),
  getById: (id) => axios.get(`/manufacturer/${id}`),
  add: (m) => axios.post('/manufacturer', m),
  update: (m) => axios.put('/manufacturer', m),
  delete: (id) => axios.delete(`/manufacturer/${id}`),
  exportExcel: () => axios.get('/manufacturer/export', { responseType: 'blob' })
}

// 客户相关API
export const customerApi = {
  getCustomerList: (params) => axios.get('/customer/list', { params: params || {} }),
  getCustomerPage: (page, size, params) => axios.get('/customer/page', { params: { page, size, ...params } }),
  getCustomerById: (id) => axios.get(`/customer/${id}`),
  addCustomer: (customer) => axios.post('/customer', customer),
  updateCustomer: (customer) => axios.put('/customer', customer),
  deleteCustomer: (id) => axios.delete(`/customer/${id}`),
  batchDeleteCustomers: (ids) => axios.post('/customer/batch-delete', { ids }),
  toggleDormant: (id, dormantStatus) => axios.put(`/customer/dormant/${id}`, { dormantStatus }),
  updateForbidden: (id, forbiddenProducts) => axios.put(`/customer/forbidden/${id}`, { forbiddenProducts }),
  exportCustomers: (params) => axios.get('/customer/export', { params }),
  getCustomerInventory: (id, params) => axios.get(`/customer/${id}/inventory`, { params }),
  getCustomerRecords: (id, params) => axios.get(`/customer/${id}/records`, { params })
}

// 订单相关API
export const orderApi = {
  getOrderPage: (page, size, params) => axios.get('/order/page', { params: { page, size, ...(params || {}) } }),
  getOrderLines: (page, size, params) => axios.get('/order/lines', { params: { page, size, ...params } }),
  getOrderById: (id) => axios.get(`/order/${id}`),
  addOrder: (order) => axios.post('/order', order),
  updateOrder: (order) => axios.put('/order', order),
  deleteOrder: (id) => axios.delete(`/order/${id}`)
}

// 出库申请相关API（page 支持 outboundCategory=product/gift 区分产品/礼品审批）
export const outboundApi = {
  getOutboundList: () => axios.get('/outbound/list'),
  getOutboundPage: (page, size, params) => axios.get('/outbound/page', { params: { page, size, ...(params || {}) } }),
  getNormalOrderPage: (page, size, params) => axios.get('/outbound/normal-page', { params: { page, size, ...(params || {}) } }),
  getVipCardPage: (page, size, params) => axios.get('/outbound/vip-card-page', { params: { page, size, ...(params || {}) } }),
  getOutboundById: (id) => axios.get(`/outbound/${id}`),
  submitOutbound: (application) => axios.post('/outbound/submit', application),
  approveOutbound: (id, status, remark, approveDate, batches, dispatchDate) => axios.post('/outbound/approve', { id, status, remark, approveDate, batches, dispatchDate }),
  finishOutbound: (id, images, customerSignature, employeeSignature) => axios.post('/outbound/finish', { id, images, customerSignature, employeeSignature }),
  payOutbound: (id) => axios.post('/outbound/pay', { id }),
  deleteOutbound: (id) => axios.delete(`/outbound/${id}`),
  // 新出库库房
  getNewOutboundStock: (name) => axios.get('/product/new-outbound-stock', { params: { name } }),
  newOutboundStockIn: (productId, quantity) => axios.post('/product/new-outbound-stock/in', { productId, quantity })
}

// 库存相关API（支持 productType=product/gift 过滤）
export const inventoryApi = {
  getInventoryList: () => axios.get('/inventory/list'),
  getInventoryPage: (page, size, params) => axios.get('/inventory/page', { params: { page, size, ...(params || {}) } }),
  inStock: (data) => axios.post('/inventory/in', data),
  inStockProduct: (data) => axios.post('/inventory/in/product', data),
  inStockAny: (data) => axios.post('/inventory/in/any', data),
  updateRecord: (id, data) => axios.put(`/inventory/${id}`, data),
  getQuantityLogs: (id) => axios.get(`/inventory/${id}/quantity-logs`),
  outStock: (data) => axios.post('/inventory/out', data),
  getStock: (productName, productType) => axios.get('/inventory/stock', { params: { productName, productType } }),
  exportInbound: (params) => axios.get('/inventory/export', { params, responseType: 'blob' }),
  getBatches: (productId) => axios.get('/inventory/batches', { params: { productId } }),
  getProductStock: (productName) => axios.get('/inventory/product-stock', { params: { productName } }),
  exportProductStock: (productName) => axios.get('/inventory/product-stock/export', { params: { productName }, responseType: 'blob' }),
  getGiftStock: (productName) => axios.get('/inventory/gift-stock', { params: { productName } }),
  exportGiftStock: (productName) => axios.get('/inventory/gift-stock/export', { params: { productName }, responseType: 'blob' }),
  getAllStock: (productName, categoryId) => axios.get('/inventory/all-stock', { params: { productName, categoryId } }),
  exportAllStock: (productName, categoryId) => axios.get('/inventory/all-stock/export', { params: { productName, categoryId }, responseType: 'blob' })
}

// 用户相关API
export const userApi = {
  getUserList: () => axios.get('/user/list'),
  getUserPage: (page, size, username, roleId, status) => axios.get('/user/page', { params: { page, size, username, roleId, status } }),
  getUserById: (id) => axios.get(`/user/${id}`),
  addUser: (user) => axios.post('/user', user),
  updateUser: (user) => axios.put('/user', user),
  deleteUser: (id) => axios.delete(`/user/${id}`),
  updateStatus: (id, status) => axios.put(`/user/status/${id}`, { status }),
  getOutboundTypes: () => axios.get('/user/outbound-types'),
  getOutboundCodes: (id) => axios.get(`/user/${id}/outbound-codes`),
  saveOutboundCodes: (id, codes) => axios.put(`/user/${id}/outbound-codes`, { codes }),
  getFuncPermissions: () => axios.get('/user/func-permissions'),
  getFuncCodes: (id) => axios.get(`/user/${id}/func-codes`),
  saveFuncCodes: (id, codes) => axios.put(`/user/${id}/func-codes`, { codes })
}

// 报表统计相关API（后台）
export const reportApi = {
  // 后台出库报表：outboundType/dateType(day|month|year|all)/date + 数据范围(超管不传 / 行政 mineForUserId / 业务员 salesmanUserId)
  getOutboundReport: (params) => axios.get('/outbound/report', { params }),
  // 当前登录人可见的出库类型中文列表（按姓名查 user_permission）
  getOutboundTypesByName: (name) => axios.get('/user/outbound-types-by-name', { params: { name } })
}

// 角色相关API
export const roleApi = {
  getRoleList: () => axios.get('/role/list'),
  getRoleById: (id) => axios.get(`/role/${id}`),
  addRole: (role) => axios.post('/role', role),
  updateRole: (role) => axios.put('/role', role),
  deleteRole: (id) => axios.delete(`/role/${id}`),
  getRoleFuncCodes: (id) => axios.get(`/role/${id}/func-codes`),
  saveRoleFuncCodes: (id, codes) => axios.put(`/role/${id}/func-codes`, { codes })
}

// 操作日志相关API
export const logApi = {
  getLogPage: (page, size, params) => axios.get('/log/page', { params: { page, size, ...params } }),
  batchDelete: (ids) => axios.delete('/log/batch', { data: ids })
}

// 数据备份相关API
export const backupApi = {
  getBackupPage: (page, size) => axios.get('/backup/page', { params: { page, size } }),
  createBackup: (operator) => axios.post('/backup/create', { operator }),
  restoreBackup: (id) => axios.post(`/backup/restore/${id}`)
}

// 系统设置相关API
export const configApi = {
  getConfigs: () => axios.get('/system-config/get'),
  saveConfigs: (data) => axios.post('/system-config/save', data)
}

// 菜单管理相关API
export const menuApi = {
  getAll: () => axios.get('/menu/all'),
  getEnabled: () => axios.get('/menu/enabled'),
  createMenu: (item) => axios.post('/menu', item),
  updateMenu: (item) => axios.put('/menu', item),
  deleteMenu: (id) => axios.delete('/menu/' + id)
}

// 导入导出相关API
export const exportApi = {
  exportCustomers: (ids) => axios.get('/export/customer', { params: (ids && ids.length) ? { ids: ids.join(',') } : {}, responseType: 'blob' }),
  exportCustomerFull: (params) => axios.get('/export/customer-full', { params, responseType: 'blob' }),
  exportInventory: () => axios.get('/export/inventory', { responseType: 'blob' }),
  importCustomers: (file) => {
    const formData = new FormData()
    formData.append('file', file)
    return axios.post('/export/customer/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
  }
}

// 客户提示相关API
export const alertApi = {
  getAlertPage: (page, size, params) => axios.get('/alert/list', { params: { page, size, ...(params || {}) } }),
  getAlertById: (id) => axios.get(`/alert/${id}`),
  addAlert: (alert) => axios.post('/alert', alert),
  updateAlert: (alert) => axios.put('/alert', alert),
  handleAlert: (id, data) => axios.put(`/alert/handle/${id}`, data),
  deleteAlert: (id) => axios.delete(`/alert/${id}`)
}

// 礼品名单管理相关API
export const giftRecipientApi = {
  getPage: (params) => axios.get('/gift-recipient/page', { params }),
  getList: (params) => axios.get('/gift-recipient/list', { params }),
  getById: (id) => axios.get(`/gift-recipient/${id}`),
  add: (data) => axios.post('/gift-recipient', data),
  update: (data) => axios.put('/gift-recipient', data),
  delete: (id) => axios.delete(`/gift-recipient/${id}`),
  batchDelete: (ids) => axios.post('/gift-recipient/batch-delete', { ids }),
  export: (params) => axios.get('/gift-recipient/export', { params, responseType: 'blob' }),
  downloadTemplate: () => axios.get('/gift-recipient/template', { responseType: 'blob' }),
  checkDuplicate: (data) => axios.post('/gift-recipient/check-duplicate', data),
  import: (file) => {
    const formData = new FormData()
    formData.append('file', file)
    return axios.post('/gift-recipient/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  importPreview: (file) => {
    const formData = new FormData()
    formData.append('file', file)
    return axios.post('/gift-recipient/import-preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  importConfirm: (data) => axios.post('/gift-recipient/import-confirm', data),
  getBatches: () => axios.get('/gift-recipient/import/batches'),
  revert: (batchNo) => axios.post('/gift-recipient/import/revert?batchNo=' + encodeURIComponent(batchNo))
}

// 个人待办相关API
export const taskApi = {
  getTaskPage: (userId, page, size, params) => axios.get('/task', { params: { userId, page, size, ...(params || {}) } }),
  getTaskById: (id) => axios.get(`/task/${id}`),
  addTask: (task) => axios.post('/task', task),
  updateTask: (task) => axios.put('/task', task),
  finishTask: (id) => axios.put(`/task/finish/${id}`),
  deleteTask: (id) => axios.delete(`/task/${id}`)
}

// 文件上传相关API
export const fileApi = {
  upload: (file) => {
    const formData = new FormData()
    formData.append('file', file)
    return axios.post('/file/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  uploadMultiple: (files) => {
    const formData = new FormData()
    files.forEach(f => formData.append('files', f))
    return axios.post('/file/upload/multiple', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  uploadBase64: (data, type) => axios.post('/file/upload/base64', { data, type })
}

export const agreementLogApi = {
  list: (bizType, bizId) => axios.get('/agreement-log', { params: { bizType, bizId } })
}

export const legacyImportApi = {
  import: (file, dryRun) => {
    const formData = new FormData()
    formData.append('file', file)
    return axios.post('/import/legacy?dryRun=' + (dryRun ? 'true' : 'false'), formData, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  revert: (batchNo) => axios.post('/import/legacy/revert?batchNo=' + encodeURIComponent(batchNo)),
  getBatches: () => axios.get('/import/legacy/batches')
}

// 短信相关API（速码信息通/凌凯）
export const smsApi = {
  sendTrialCode: (phone) => axios.post('/sms/send-trial-code', { phone }),
  verifyTrialCode: (phone, code) => axios.post('/sms/verify-trial-code', { phone, code }),
  getBalance: () => axios.get('/sms/balance'),
  testSend: (phone) => axios.post('/sms/test-send', { phone })
}

